import { api } from '../api/client';
import type { UnifiedInvoiceRow } from '../types/unified-invoice';

/**
 * Finance → Invoices is the ONE invoice list across verticals (decided April
 * 2026). Casing rows come from GET /invoices exactly as before; every other
 * billing vertical's rows come from GET /unified-invoices?vertical=<key> and are
 * merged in the browser.
 *
 * Merged client-side ON PURPOSE, and only while no tenant holds casing AND
 * another billing vertical (true on 2026-10-08). The day one does, move this
 * to a server-side combined list: client merging cannot page across sources.
 */

/** The non-casing billing verticals, in chip order. Keys are companies.enabled_verticals keys. */
export type OtherBillingVertical = 'pressure_testing' | 'downhole_tools' | 'trucking' | 'surface_rentals' | 'ndt_inspection' | 'pipeline_facility' | 'well_testing' | 'well_servicing' | 'earthworks';

export const CASING_BILLING_VERTICAL = 'casing_running';

interface BillingVerticalConfig {
  label: string;
  /** SOURCE_BADGE key the row's badge renders with. */
  badge: string;
  /** Statuses this vertical stores that land on the Approved tab. */
  usesApproved: boolean;
  /** Where a row opens. Null: the vertical has no invoice detail screen yet — the row is not clickable. */
  detailPath: ((id: string) => string) | null;
}

export const OTHER_BILLING_VERTICALS: Record<OtherBillingVertical, BillingVerticalConfig> = {
  pressure_testing: {
    label: 'Pressure Testing',
    badge: 'pt_field_ticket',
    usesApproved: true,
    // The PT invoice's own page (a legacy ?invoice= link is forwarded there).
    detailPath: (id) => `/pressure-testing/invoices/${encodeURIComponent(id)}`,
  },
  downhole_tools: {
    label: 'Downhole',
    badge: 'downhole_run',
    usesApproved: true,
    detailPath: (id) => `/downhole/invoices/${encodeURIComponent(id)}`,
  },
  trucking: {
    label: 'Trucking',
    badge: 'trucking',
    usesApproved: true,
    detailPath: (id) => `/trucking/invoices/${encodeURIComponent(id)}`,
  },
  surface_rentals: {
    label: 'Rentals',
    badge: 'rental_billing',
    usesApproved: false,
    detailPath: (id) => `/rentals/invoices/${encodeURIComponent(id)}`,
  },
  // NDT batch 5 (fieldiq-api#1166, view branch 0540): source_type ndt_field_ticket.
  ndt_inspection: {
    label: 'NDT',
    badge: 'ndt_field_ticket',
    usesApproved: true,
    detailPath: (id) => `/ndt-inspection/invoices/${encodeURIComponent(id)}`,
  },
  // PF-5c (fieldiq-api#1232, view branch 0560): source_type pf_field_ticket.
  // The row opens the Pipeline invoice page, where Send, Mark paid (Finance's
  // PATCH) and Void (its own door — Finance's PATCH refuses `void` with
  // status_owned_by_dedicated_endpoint) all live, as NDT's do.
  pipeline_facility: {
    label: 'Pipeline',
    badge: 'pf_field_ticket',
    usesApproved: true,
    detailPath: (id) => `/pipeline-facility/invoices/${encodeURIComponent(id)}`,
  },
  // WT-5 (fieldiq-api view branch 0572): source_type wt_field_ticket. wt_invoices
  // stores draft | sent | approved | paid | void.
  well_testing: {
    label: 'Well Testing',
    badge: 'wt_field_ticket',
    usesApproved: true,
    detailPath: (id) => `/well-testing/invoices/${encodeURIComponent(id)}`,
  },
  // WS-5 (fieldiq-api#1295, view branch 0596): source_type ws_field_ticket.
  // ws_invoices stores draft | sent | approved | paid | void. The row opens the
  // Well Servicing invoice page, where Send, Mark paid (Finance's PATCH) and
  // Void (its own door) live.
  well_servicing: {
    label: 'Well Servicing',
    badge: 'ws_field_ticket',
    usesApproved: true,
    detailPath: (id) => `/well-servicing/invoices/${encodeURIComponent(id)}`,
  },
  // EW-6 (fieldiq-api#1300, view branch 0599): source_type is 'ew_' || kind
  // (ew_standard, ew_from_quote, ew_progress_draw, ew_holdback_release,
  // ew_change_order), so
  // the badge is one per vertical, not per source. ew_invoices stores
  // draft | sent | paid | void — no approved. The row opens the Earthworks
  // invoice page, where Mark sent, Record payment and Void live; Finance's
  // PATCH {status:'paid'} records the balance as a payment (markEwInvoicePaid).
  earthworks: {
    label: 'Earthworks',
    badge: 'ew_invoice',
    usesApproved: false,
    detailPath: (id) => `/earthworks/invoices/${encodeURIComponent(id)}`,
  },
};

export const OTHER_BILLING_VERTICAL_KEYS = Object.keys(OTHER_BILLING_VERTICALS) as OtherBillingVertical[];

export function isOtherBillingVertical(key: string | null | undefined): key is OtherBillingVertical {
  return !!key && Object.prototype.hasOwnProperty.call(OTHER_BILLING_VERTICALS, key);
}

/** The view's page cap (unified-invoices.routes.ts clamps limit to 100). */
const PAGE_LIMIT = 100;
/** A runaway guard, not a business limit: 50 × 100 rows. */
const MAX_PAGES = 50;

/**
 * Every row of one vertical, paged until `pagination.total` is reached. The
 * screen's tabs filter client-side, so it needs the whole set, not one page.
 * A failure rejects — the screen renders it, never an empty list.
 */
export async function fetchAllUnifiedInvoices(vertical: OtherBillingVertical): Promise<UnifiedInvoiceRow[]> {
  const rows: UnifiedInvoiceRow[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await api.unifiedInvoices.list({ vertical, page, limit: PAGE_LIMIT });
    const body = res.data;
    const data = Array.isArray(body?.data) ? body.data : [];
    rows.push(...data);
    const total = body?.pagination?.total;
    if (data.length < PAGE_LIMIT || (typeof total === 'number' && rows.length >= total)) break;
  }
  return rows;
}
